import React, { useEffect, useRef } from 'react';
import { ChatMessage as ChatMessageType } from '../../types/chat';
import { ChatMessage } from './ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';

/**
 * ChatMessageList - Scrollable message thread with auto-scroll
 *
 * Requirements:
 * - 1.3: Messages displayed in scrollable thread
 * - 1.4: Message alignment by role (user right, assistant left)
 */

interface ChatMessageListProps {
  messages: ChatMessageType[];
}

/**
 * Welcome message displayed when no messages exist
 */
const WelcomeMessage: React.FC = () => (
  <div className="flex h-full flex-col items-center justify-center px-4 text-center">
    <div className="mb-4 rounded-full bg-primary/10 p-3 dark:bg-primary/10">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8 text-primary"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    </div>
    <h3 className="mb-2 text-lg font-medium text-foreground">
      FinOps AI Assistant
    </h3>
    <p className="max-w-xs text-sm text-muted-foreground">
      Ask me about your cloud costs, anomalies, optimization recommendations, or spending forecasts.
    </p>
  </div>
);

export const ChatMessageList: React.FC<ChatMessageListProps> = ({ messages }) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages are added or content changes (streaming)
  useEffect(() => {
    if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <WelcomeMessage />
        <div className="mt-6 px-4">
          <SuggestedQuestions />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex h-full flex-col space-y-3 overflow-y-auto"
      role="list"
      aria-label="Chat messages"
    >
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} />
      ))}
      <div ref={bottomRef} aria-hidden="true" />
    </div>
  );
};
